import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import type {CoreEntityType} from 'nutshell-core/types';

import {colors} from 'shells/colors/colors';
import {Illustration} from 'shells/illustrations/illustration';
import {Link} from 'shells/link/link';
import {Modal} from 'shells/modal/modal';
import {OnboardingBody} from 'shells/onboarding-page/onboarding-body';
import {OnboardingPage} from 'shells/onboarding-page/onboarding-page';

import {SwoopArrow} from '../../onboarding/lifting-of-the-curtain/slide-team/swoop-arrow';

type Props = {
    entityType: CoreEntityType;
    audience: {
        name: string;
        htmlUrlPath: string;
    };
    onClose: () => void;
};

function ImageWrapper({image}: {image: React.ReactNode}) {
    return (
        <div className='full-width full-height pad-64 border-box align-center justify-center flex'>
            {image}
        </div>
    );
}

export function AddToAudienceOnboardingModal(props: Props) {
    return (
        <Modal
            isOpen={true}
            onClose={props.onClose}
            noPadding={true}
            closeButton={false}
            maxHeight={500}
            trapFocus={true}
        >
            <OnboardingPage
                type='singlePage'
                bodyId='addToAudience'
                bodyComponent={getBodyComponent(props.entityType, props.onClose, props.audience)}
                artBackgroundColor={colors.plumLt}
                artImage={<ImageWrapper image={<Illustration src='audience' />} />}
                buttonText='Got it'
                onClose={props.onClose}
            />
            <div id='arrow-start'></div>
            {/* @ts-expect-error */}
            <SwoopArrow startSelector='arrow-start' destinationSelector='add-to-audience-button' />
        </Modal>
    );
}

function getBodyComponent(
    entityType: CoreEntityType,
    onClose: () => void,
    audience: {
        name: string;
        htmlUrlPath: string;
    }
) {
    switch (entityType) {
        case 'leads':
            return <AddToAudienceOnboardingBodyLeads onClose={onClose} audience={audience} />;
        case 'accounts':
            return <AddToAudienceOnboardingBodyAccounts onClose={onClose} audience={audience} />;
        case 'contacts':
            return <AddToAudienceOnboardingBodyContacts onClose={onClose} audience={audience} />;
    }
}

function AddToAudienceOnboardingBodyContacts({
    onClose,
    audience,
}: {
    onClose: () => void;
    audience: {
        name: string;
        htmlUrlPath: string;
    };
}) {
    return (
        <OnboardingBody title={`Add people to ${audience.name}`}>
            <div>
                <p>
                    You can add people to your Audience right from your list pages! Individually
                    select people, filter by specific criteria, or use one of your saved lists.
                </p>
                <p>1. Select some people</p>
                <p>2. Click &quot;Add to Audience&quot;</p>
                <p>
                    If you’d rather start from your {/* @ts-expect-error */}
                    <Link as={RoutedLink} onClick={onClose} variant='primary' to='/leads'>
                        Lead
                    </Link>{' '}
                    or {/* @ts-expect-error */}
                    <Link as={RoutedLink} onClick={onClose} variant='primary' to='/companies'>
                        Company
                    </Link>{' '}
                    lists, you can do that too!
                </p>
            </div>
        </OnboardingBody>
    );
}

function AddToAudienceOnboardingBodyAccounts({
    onClose,
    audience,
}: {
    onClose: () => void;
    audience: {
        name: string;
        htmlUrlPath: string;
    };
}) {
    return (
        <OnboardingBody title={`Add people to ${audience.name}`}>
            <div>
                <p>
                    You can add people related to these companies to your Audience right from your
                    list pages! Individually select companies, filter by specific criteria, or use
                    one of your saved lists.
                </p>
                <p>1. Select some companies</p>
                <p>2. Click &quot;Add to Audience&quot;</p>
                <p>
                    If you’d rather start from your {/* @ts-expect-error */}
                    <Link as={RoutedLink} onClick={onClose} variant='primary' to='/people'>
                        People
                    </Link>{' '}
                    or {/* @ts-expect-error */}
                    <Link as={RoutedLink} onClick={onClose} variant='primary' to='/leads'>
                        Lead
                    </Link>{' '}
                    lists, you can do that too!
                </p>
            </div>
        </OnboardingBody>
    );
}

function AddToAudienceOnboardingBodyLeads({
    onClose,
    audience,
}: {
    onClose: () => void;
    audience: {
        name: string;
        htmlUrlPath: string;
    };
}) {
    return (
        <OnboardingBody title={`Add people to ${audience.name}`}>
            <div>
                <p>
                    You can add people related to these leads to your Audience right from your list
                    pages! Individually select leads, filter by specific criteria, or use one of
                    your saved lists.
                </p>
                <p>1. Select some leads</p>
                <p>2. Click &quot;Add to Audience&quot;</p>
                <p>
                    If you’d rather start from your {/* @ts-expect-error */}
                    <Link as={RoutedLink} onClick={onClose} variant='primary' to='/people'>
                        People
                    </Link>{' '}
                    or {/* @ts-expect-error */}
                    <Link as={RoutedLink} onClick={onClose} variant='primary' to='/companies'>
                        Company
                    </Link>{' '}
                    lists, you can do that too!
                </p>
            </div>
        </OnboardingBody>
    );
}
